import { EyeIcon } from "@xola/icons";
import clsx from "clsx";
import { useFormikContext } from "formik";
import React, { useCallback } from "react";
import { Experience, ReviewDestination, ReviewPreference } from "types";
import { PreviewMessageTemplateDrawer } from "../Customers/Templates/PreviewMessageTemplateDrawer";
import { RichEditor } from "@/components/Common/RichEditor";
import { EmailVariables, variableItems, Variables } from "@/components/Common/RichEditor/Variable.type";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { usePreviewReviewTemplate } from "@/hooks/previewReviewReminderHooks";
import { useVisibility } from "@/hooks/useVisibility";

export const CustomReviewEmailTemplate = ({ experience }: { experience?: Experience }) => {
    const [visible, { toggle }] = useVisibility(false);
    const { values, setFieldValue, errors } = useFormikContext<ReviewPreference>();

    const { mutateAsync: previewTemplate, data: templatePreview } = usePreviewReviewTemplate(experience, () =>
        toggle(),
    );

    const handlePreviewClick = useCallback(() => {
        previewTemplate(values.reviewTemplate ?? "");
    }, [previewTemplate, values.reviewTemplate]);

    if (values.destination !== ReviewDestination.CUSTOM) return null;

    return (
        <ContentLayout.Section
            title="Email Template"
            description="Create a custom email template to send to your customers"
        >
            <div className="space-y-4">
                <span className={clsx(errors?.reviewTemplate && "!text-danger", "text-sm font-bold")}>
                    {errors?.reviewTemplate ? "Email Template is required" : ""}
                </span>
                <RichEditor
                    showVariableButton
                    initialContent={values.reviewTemplate ?? ""}
                    variables={variableItems.filter((v) => EmailVariables.has(v.name as Variables))}
                    onChange={(html) => {
                        setFieldValue("reviewTemplate", html);
                    }}
                />
                <SecondaryButton size="small" className="mr-auto" icon={<EyeIcon />} onClick={handlePreviewClick}>
                    Preview
                </SecondaryButton>
            </div>
            <PreviewMessageTemplateDrawer isOpen={visible} onClose={toggle}>
                {templatePreview && (
                    <iframe sandbox="allow-same-origin" srcDoc={templatePreview} className="h-full w-full" />
                )}
            </PreviewMessageTemplateDrawer>
        </ContentLayout.Section>
    );
};
